import { useRef, useState } from 'react'
import { useTaskboard } from '../lib/taskboardContext.js'
import { dueMeta, bellMeta, tsToDate, filterTasks } from '../lib/format.js'
import Avatar from '../components/Avatar.jsx'
import { Chip } from '../ds/index.js'
import useEnterStagger from '../ds/hooks/useEnterStagger.js'
import { ChatCircle, CaretDown } from '@phosphor-icons/react'

const PHASE_LABEL = { p1: '30', p2: '60', p3: '90' }

// Ceiling on the open panel's row well: a long section scrolls inside it rather
// than stretching the page, a short one shrinks to its rows. Clamped rather than
// a bare px so a phone doesn't hand its whole viewport to one panel.
const PANEL_MAX_HEIGHT = 'clamp(240px, 42vh, 480px)'

// One section's row list. Extracted for readability; the entrance stagger lives
// on the parent's stable container (not here), so collapse/expand — which
// mounts/unmounts this — never replays the animation.
function SectionRows({
  sectionId, sectionTasks, usersById, labelsById, statusesById, openTask, toggleDone,
  addingSection, addDraft, setAddDraft, setAddingSection, submitAdd,
}) {
  return (
    <div style={{ border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-lg)', background: 'var(--color-panel-dark)', overflow: 'hidden', marginTop: 8, display: 'flex', flexDirection: 'column' }}>
      {/* overflowX stays hidden: nothing here may scroll sideways. A row no
          longer needs to — .tb-taskrow (src/index.css) wraps its trailing
          metadata onto a second line once the row itself gets narrow, so the
          title keeps line 1 instead of being squeezed to 0px by the chips. */}
      <div style={{ maxHeight: PANEL_MAX_HEIGHT, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
        {sectionTasks.length === 0 && (
          <div style={{ padding: '26px 18px', color: 'var(--color-muted)', fontSize: 14 }}>
            No tasks in this section.
          </div>
        )}
        {sectionTasks.map((t) => {
          const done = !!statusesById[t.status]?.done
          const assignee = usersById[t.assigneeUid]
          const meta = dueMeta(tsToDate(t.dueDate), done)
          const bell = bellMeta(meta, done)
          const rowLabels = (t.labelIds || []).filter((id) => labelsById[id])
          // Drives whether the trailing block renders at all. On a phone that
          // block is a whole second line, so a row with nothing to show in it
          // must not pay for one.
          const hasMeta = rowLabels.length > 0 || !!t.phase || t.commentCount > 0 || meta.hasDue
          return (
            <div
              key={t.id}
              className="tb-taskrow"
              onClick={() => openTask(t.id)}
              style={{ padding: '14px 18px', minHeight: 56, borderBottom: '1px solid var(--surface-row)', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(200,168,75,.04)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {/* Padding + negative margin widen the tap target past the 22px
                  dot without changing how the row reads. */}
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  toggleDone(t)
                }}
                style={{ display: 'flex', padding: 6, margin: -6, flexShrink: 0, cursor: 'pointer' }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: '1.5px solid ' + (done ? 'var(--color-gold)' : '#3a3527'),
                    background: done ? 'var(--color-gold)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {done && <span style={{ fontSize: 12, lineHeight: 1, fontWeight: 700, color: 'var(--color-bg-dark)' }}>✓</span>}
                </div>
              </div>
              {bell.show && <span style={bell.style}>{bell.glyph}</span>}
              <span className="tb-taskrow-title" style={done ? { fontSize: 15, textDecoration: 'line-through', color: 'var(--text-dim)' } : { fontSize: 15, color: 'var(--color-white)' }}>
                {t.title}
              </span>
              {/* Avatar sits outside the wrapping metadata block on purpose —
                  see .tb-taskrow in src/index.css. */}
              <span className="tb-taskrow-avatar">
                <Avatar user={assignee} size={28} />
              </span>
              {/* No status chip. The accordion header names this section's
                  status and only one section's rows are mounted at a time, so
                  the chip was pure duplication — and the widest of the
                  flex-shrink:0 items competing with the title. */}
              {hasMeta && (
                <div className="tb-taskrow-meta">
                  <div className="tb-taskrow-chips">
                    {rowLabels.map((id) => (
                      <Chip key={id} variant="solid" color={labelsById[id].color}>{labelsById[id].name}</Chip>
                    ))}
                    {t.phase && (
                      <Chip>Phase · {PHASE_LABEL[t.phase]}</Chip>
                    )}
                  </div>
                  {t.commentCount > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-muted)', fontSize: 11, flexShrink: 0, fontFamily: 'var(--font-mono)' }}>
                      <ChatCircle size={13} weight="fill" />
                      {t.commentCount}
                    </span>
                  )}
                  {meta.hasDue && (
                    <span style={{ color: meta.overdue ? 'var(--color-danger)' : 'var(--color-muted)', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'var(--font-mono)' }}>
                      {meta.label}
                    </span>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Add row sits below the scroll well so it stays reachable however far
          down a long section is scrolled. */}
      <div style={{ borderTop: '1px solid var(--color-divider-dark)', flexShrink: 0 }}>
        {addingSection === sectionId ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px', minHeight: 54, background: 'rgba(200,168,75,.04)' }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px dashed var(--border-dash)', flexShrink: 0 }} />
            <input
              className="cn-input"
              value={addDraft}
              onChange={(e) => setAddDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitAdd(sectionId)
                else if (e.key === 'Escape') {
                  setAddingSection(null)
                  setAddDraft('')
                }
              }}
              autoFocus
              placeholder="Task name — Enter to add, Esc to cancel"
              style={{ flex: 1, fontSize: 15, border: 'none', outline: 'none', background: 'transparent', color: 'var(--color-white)' }}
            />
          </div>
        ) : (
          <div
            onClick={() => {
              setAddingSection(sectionId)
              setAddDraft('')
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '15px 18px', minHeight: 54, color: 'var(--color-muted)', cursor: 'pointer', fontSize: 14 }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--color-gold)'
              e.currentTarget.style.background = 'rgba(200,168,75,.04)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--color-muted)'
              e.currentTarget.style.background = 'transparent'
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add task
          </div>
        )}
      </div>
    </div>
  )
}

export default function ListView({ tasks, labelFilter, assigneeFilter, phaseFilter, search, incompleteOnly, dueSoonOnly, openSection, onSetSection }) {
  const { usersById, labelsById, statuses, statusesById, openTask, toggleDone, quickAddTask } = useTaskboard()
  const [addingSection, setAddingSection] = useState(null)
  const [addDraft, setAddDraft] = useState('')

  const projTasks = filterTasks(tasks, { labelFilter, assigneeFilter, phaseFilter, incompleteOnly, dueSoonOnly, search, statusesById })

  const submitAdd = (key) => {
    if (addDraft.trim()) quickAddTask(addDraft, key)
    setAddDraft('')
  }

  // Accordion: one panel open at a time. null/undefined from the owner means
  // "nothing picked yet" — resolved to the first section so the view never lands
  // fully collapsed; '' means the user closed the one that was open.
  const openId = openSection == null ? statuses[0]?.id : openSection

  // Resolve here rather than toggling blind in the owner: the owner can't know
  // which section the null default lands on.
  const selectSection = (id) => {
    setAddingSection(null)
    setAddDraft('')
    onSetSection(openId === id ? '' : id)
  }

  // Stagger the status-section blocks. Anchored to the stable outer container
  // (always mounted; section headers never unmount on collapse), so the one-shot
  // played guard survives collapse/expand and filter changes — it plays once per
  // view load. dep = section count (0→N once statuses stream in).
  const listRef = useRef(null)
  useEnterStagger(listRef, { dep: statuses.length })

  return (
    <div ref={listRef}>
      {statuses.map((s) => {
        const sectionTasks = projTasks.filter((t) => t.status === s.id)
        const expanded = openId === s.id
        return (
          <div key={s.id} style={{ marginBottom: 14 }}>
            <div
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              onClick={() => selectSection(s.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  selectSection(s.id)
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', minHeight: 56, borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-divider-dark)', background: expanded ? 'rgba(200,168,75,.05)' : 'var(--color-panel-dark)', cursor: 'pointer', userSelect: 'none' }}
            >
              <CaretDown size={14} weight="bold" style={{ transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform .15s', color: 'var(--color-muted)', flexShrink: 0 }} />
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
              <span className="font-display" style={{ fontSize: 17, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--color-white)', whiteSpace: 'nowrap' }}>
                {s.name}
              </span>
              <span className="font-mono" style={{ fontSize: 12, color: 'var(--color-muted)' }}>{sectionTasks.length}</span>
            </div>

            {expanded && (
              <SectionRows
                sectionId={s.id}
                sectionTasks={sectionTasks}
                usersById={usersById}
                labelsById={labelsById}
                statusesById={statusesById}
                openTask={openTask}
                toggleDone={toggleDone}
                addingSection={addingSection}
                addDraft={addDraft}
                setAddDraft={setAddDraft}
                setAddingSection={setAddingSection}
                submitAdd={submitAdd}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
